<template>
  <transition name="search-megamenu" appear>
    <div
      v-if="open && !isMobileOrTablet"
      ref="searchMenuRef"
      class="ca-header__search-megamenu js-search-area"
      :style="searchBarStyle"
    >
      <hr
        class="ca-header__megamenu-separator mt-size-5 mb-size-0 ml-size-4 mr-size-4"
      />
      <div
        class="ca-header__megamenu-inner pt-size-5 pb-size-4"
        :class="{ 'ca-header__megamenu-inner--loading': autocompleteLoading }"
      >
        <!-- Loading state -->
        <ul v-if="autocompleteLoading" class="ca-header__megamenu-list">
          <li v-for="n in 6" :key="n">
            <div class="ca-header__megamenu-link caption-medium">
              <span class="ca-header__autocomplete-shimmer" />
            </div>
          </li>
        </ul>

        <!-- Sugestões iniciais -->
        <ul
          v-else-if="searchQuery.trim().length < 3 && suggestions.length"
          class="ca-header__search-list"
        >
          <li
            v-for="(item, idx) in suggestions.slice(0, 6)"
            class="ca-header-search__megamenu-results pl-size-5 pr-size-4 gap-size-3"
            :key="idx"
            @click="emit('select-suggestion', item)"
          >
            <component :is="SearchIconGrey" />
            <a
              class="ca-header__megamenu-link caption-medium"
              style="cursor: pointer"
              @mouseenter="hoveredLink = `suggest-${idx}`"
              @mouseleave="hoveredLink = null"
            >
              <span>{{ item.title }}</span>
              <component
                :is="ArrowRight"
                class="ca-header__megamenu-link--arrow"
                v-if="hoveredLink === `suggest-${idx}`"
              />
            </a>
          </li>
        </ul>

        <!-- Sugestões do autocomplete -->
        <ul
          v-else-if="autocompleteResults.length"
          class="ca-header__search-list"
        >
          <li
            v-for="(result, idx) in autocompleteResults.slice(0, 6)"
            class="ca-header-search__megamenu-results pt-size-3 pb-size-3 pl-size-5 pr-size-4 gap-size-3"
            :key="idx"
            @click="emit('select-suggestion', result)"
          >
            <component :is="SearchIconGrey" />
            <a
              class="ca-header__megamenu-link caption-medium"
              style="cursor: pointer"
              @mouseenter="hoveredLink = `auto-${idx}`"
              @mouseleave="hoveredLink = null"
            >
              <span>
                {{ highlight(result.label).matched
                }}<span style="font-weight: 700">{{
                  highlight(result.label).unmatched
                }}</span>
              </span>
              <component
                :is="ArrowRight"
                class="ca-header__megamenu-link--arrow"
                v-if="hoveredLink === `auto-${idx}`"
              />
            </a>
          </li>
        </ul>

        <ul v-else>
          <li
            class="ca-header-search__megamenu-results pt-size-3 pb-size-3 pl-size-5 pr-size-4 gap-size-3 ca-header-search__megamenu-results--none"
          >
            <span class="ca-header__search__no-result caption-medium"
              >Nenhum resultado...</span
            >
          </li>
        </ul>
      </div>

      <hr
        v-if="autocompleteResults.length > 6"
        class="ca-header__megamenu-separator mt-size-5 mb-size-0 ml-size-4 mr-size-4"
      />
      <div
        v-if="autocompleteResults.length > 6"
        style="display: flex; justify-content: center; padding: 20px"
      >
        <LinkButton
          type="default"
          @click="emit('show-all')"
          label="Ver todos os resultados"
        />
      </div>
    </div>
  </transition>
</template>

<script setup>
import SearchIconGrey from "@assets/search-icon-grey.svg";
import ArrowRight from "@assets/arrow-right-green.svg";
import { ref } from "vue";
import { LinkButton } from "ca-online-design-system";

const props = defineProps({
  open: { type: Boolean, default: false },
  isMobileOrTablet: { type: Boolean, default: false },
  autocompleteLoading: { type: Boolean, default: false },
  searchQuery: { type: String, default: "" },
  suggestions: { type: Array, default: () => [] },
  autocompleteResults: { type: Array, default: () => [] },
  highlightMatchedText: { type: Function, required: true },
  searchBarStyle: { type: Object, default: () => ({}) },
});

const emit = defineEmits(["select-suggestion", "show-all"]);

const highlight = (label) =>
  props.highlightMatchedText(label, props.searchQuery);

const hoveredLink = ref(null);
</script>

<style scoped lang="scss">
.ca-header__search-megamenu {
  position: absolute;
  top: 93%;
  left: 0;
  right: 0;
  background: white;
  transform: translateY(-8px);
  z-index: 101;
  border-bottom-left-radius: var(--size-3);
  border-bottom-right-radius: var(--size-3);
  box-shadow: 0 6px 6px -2px rgba(0, 0, 0, 0.1);
}

.search-megamenu-enter-active,
.search-megamenu-leave-active {
  transition:
    opacity 0.35s ease,
    transform 0.35s ease;
}

.search-megamenu-enter-from,
.search-megamenu-leave-to {
  opacity: 0;
  transform: translateY(-16px);
}

.search-megamenu-enter-to,
.search-megamenu-leave-from {
  opacity: 1;
  transform: translateY(-8px);
}

.ca-header__megamenu-separator {
  border: none;
  border-top: 1px solid var(--ca-color-text-light);
}

.ca-header__megamenu-inner {
  margin-left: var(--search-bar-left, 108px);
  width: var(--search-bar-width, auto);
  max-width: calc(100% - var(--search-bar-left, 108px));

  &--loading {
    padding: var(--size-5) var(--size-0) var(--size-4);
  }

  ul {
    padding-inline-start: var(--size-0);
    list-style: none;
  }
}

.ca-header__megamenu-list,
.ca-header__search-list {
  display: flex;
  flex-direction: column;
  list-style: none;
  padding: 0;
  margin: 0;
}

.ca-header-search__megamenu-results {
  display: flex;
  align-items: center;
  list-style: none;

  &:not(&--none):hover {
    border-radius: var(--size-3);
    background: var(--ca-color-hover-pressed-container-high-hover);
  }

  &--none {
    // sem hover
    margin-left: 16px;
  }
}

.ca-header__megamenu-link {
  color: var(--ca-color-text-body-variant);
  letter-spacing: var(--letter-spacing-xs);
  text-decoration: none;
  display: flex;
  width: 100%;
  align-items: center;
  gap: var(--size-3);
  min-height: var(--size-7);
  cursor: pointer;

  &--arrow {
    margin-left: auto;
    pointer-events: none;
  }
}

.ca-header__megamenu-view-all {
  color: var(--ca-color-theme-primary);
  display: flex;
  align-items: center;
  gap: var(--size-3);
  padding: var(--size-3) var(--size-4);
  border-radius: var(--size-2);
  background: white;
  border: none;
  cursor: pointer;

  &:focus-visible {
    outline: 1px solid var(--ca-color-feedback-info);
  }

  &:hover {
    color: var(--ca-color-hover-pressed-primary-hover);
    text-decoration-line: underline;
  }

  &:active {
    color: var(--ca-color-hover-pressed-primary-pressed);
  }
}

.ca-header__search__no-result {
  color: var(--ca-color-text-body-variant);
}

@keyframes shimmer {
  0% {
    background-position: 200% center;
  }
  100% {
    background-position: -200% center;
  }
}

.ca-header__autocomplete-shimmer {
  display: inline-block;
  width: 100%;
  height: var(--size-7);
  border-radius: var(--size-2);
  background: linear-gradient(
    270deg,
    #fcfdfb 0%,
    var(--Colors-CAEmpresas-SolarizedMedium, #edecea) 50%,
    #fcfdfb 100%
  );
  background-size: 200% auto;
  animation: shimmer 2.5s linear infinite;
  margin-bottom: var(--size-3);
}
</style>
